'use client';

import React from 'react';
import { TrendingUp, Layers, ArrowDownToLine, ArrowUpFromLine, AlertTriangle, Percent, Zap } from 'lucide-react';
import { MaturityData, calcInstantAPR } from '../data/initialData';



interface SimulatorStatCardsProps {
  data: MaturityData;
}

const formatUSD = (v: number) => {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toLocaleString()}`;
};

export default function SimulatorStatCards({ data }: SimulatorStatCardsProps) {
  const isInstant = data.maturity === 'Instant';
  const instantMarkets = data.instantMarkets && data.instantMarkets.length > 0 ? data.instantMarkets : null;

  // For Instant: derive all stats from instantMarkets source list
  const instantAPR = instantMarkets ? calcInstantAPR(instantMarkets, 'totalBorrowed') : data.apr;
  const instantTotalSupply = instantMarkets ? instantMarkets.reduce((s, m) => s + m.totalSupply, 0) : data.totalLiquidity;
  const instantTotalBorrow = instantMarkets ? instantMarkets.reduce((s, m) => s + m.totalBorrowed, 0) : data.totalBorrowDemand;
  const instantProtocolCount = instantMarkets ? instantMarkets.length : 0;

  const effectiveLiquidity = isInstant ? instantTotalSupply : data.totalLiquidity;
  const effectiveDemand = isInstant ? instantTotalBorrow : data.totalBorrowDemand;
  const effectiveAPR = isInstant ? instantAPR : data.apr;

  const utilization = effectiveLiquidity > 0
    ? (effectiveDemand / effectiveLiquidity) * 100
    : 0;

  const bestSupplyRate = data.supplyOrders.length > 0
    ? Math.min(...data.supplyOrders.map((o) => o.rate))
    : null;
  const bestBorrowRate = data.borrowOrders.length > 0
    ? Math.min(...data.borrowOrders.map((o) => o.rate))
    : null;
  const spread = bestBorrowRate !== null && bestSupplyRate !== null
    ? bestBorrowRate - bestSupplyRate
    : null;

  const isHighUtil = utilization > 85;
  const excessDemand = effectiveDemand - effectiveLiquidity;

  const cards = isInstant ? [
    {
      id: 'apr',
      label: 'Weighted APR',
      value: `${effectiveAPR.toFixed(2)}%`,
      sub: 'Instant floating rate',
      icon: Zap,
      iconColor: 'text-[#6C47FF]',
      iconBg: 'bg-[#EEE9FF]',
      accent: 'border-l-[#6C47FF]',
      valueColor: 'text-[#6C47FF]',
      badge: null,
    },
    {
      id: 'liquidity',
      label: 'Total Supply',
      value: formatUSD(instantTotalSupply),
      sub: `${instantProtocolCount} protocol${instantProtocolCount !== 1 ? 's' : ''}`,
      icon: ArrowUpFromLine,
      iconColor: 'text-green-600',
      iconBg: 'bg-green-50',
      accent: 'border-l-green-500',
      valueColor: 'text-green-700',
      badge: null,
    },
    {
      id: 'demand',
      label: 'Total Borrowed',
      value: formatUSD(instantTotalBorrow),
      sub: `across ${instantProtocolCount} protocol${instantProtocolCount !== 1 ? 's' : ''}`,
      icon: ArrowDownToLine,
      iconColor: 'text-orange-600',
      iconBg: 'bg-orange-50',
      accent: 'border-l-orange-500',
      valueColor: 'text-orange-700',
      badge: isHighUtil ? 'High Demand' : null,
    },
    {
      id: 'utilization',
      label: 'Utilization',
      value: `${utilization.toFixed(1)}%`,
      sub: isHighUtil
        ? `${formatUSD(Math.abs(excessDemand))} excess demand`
        : `${formatUSD(instantTotalSupply - instantTotalBorrow)} headroom`,
      icon: isHighUtil ? AlertTriangle : Layers,
      iconColor: isHighUtil ? 'text-amber-600' : 'text-blue-600',
      iconBg: isHighUtil ? 'bg-amber-50' : 'bg-blue-50',
      accent: isHighUtil ? 'border-l-amber-500' : 'border-l-blue-500',
      valueColor: isHighUtil ? 'text-amber-700' : 'text-blue-700',
      badge: isHighUtil ? 'At Risk' : null,
    },
    {
      id: 'protocols',
      label: 'Protocols',
      value: instantProtocolCount > 0 ? instantProtocolCount.toString() : '—',
      sub: instantMarkets ? instantMarkets.map((m) => m.protocol).join(', ') : 'No data',
      icon: TrendingUp,
      iconColor: 'text-indigo-600',
      iconBg: 'bg-indigo-50',
      accent: 'border-l-indigo-500',
      valueColor: 'text-indigo-700',
      badge: null,
    },
  ] : [
    {
      id: 'apr',
      label: 'Fixed APR',
      value: `${data.apr.toFixed(2)}%`,
      sub: `${data.maturity} tenor`,
      icon: Percent,
      iconColor: 'text-[#6C47FF]',
      iconBg: 'bg-[#EEE9FF]',
      accent: 'border-l-[#6C47FF]',
      valueColor: 'text-[#6C47FF]',
      badge: null,
    },
    {
      id: 'liquidity',
      label: 'Total Supply',
      value: formatUSD(data.totalLiquidity),
      sub: `${data.supplyOrders.length} order levels`,
      icon: ArrowUpFromLine,
      iconColor: 'text-green-600',
      iconBg: 'bg-green-50',
      accent: 'border-l-green-500',
      valueColor: 'text-green-700',
      badge: null,
    },
    {
      id: 'demand',
      label: 'Borrow Demand',
      value: formatUSD(data.totalBorrowDemand),
      sub: `${data.borrowOrders.length} order levels`,
      icon: ArrowDownToLine,
      iconColor: 'text-orange-600',
      iconBg: 'bg-orange-50',
      accent: 'border-l-orange-500',
      valueColor: 'text-orange-700',
      badge: isHighUtil ? 'High Demand' : null,
    },
    {
      id: 'utilization',
      label: 'Utilization',
      value: `${utilization.toFixed(1)}%`,
      sub: isHighUtil
        ? `${formatUSD(Math.abs(excessDemand))} excess demand`
        : `${formatUSD(data.totalLiquidity - data.totalBorrowDemand)} headroom`,
      icon: isHighUtil ? AlertTriangle : Layers,
      iconColor: isHighUtil ? 'text-amber-600' : 'text-blue-600',
      iconBg: isHighUtil ? 'bg-amber-50' : 'bg-blue-50',
      accent: isHighUtil ? 'border-l-amber-500' : 'border-l-blue-500',
      valueColor: isHighUtil ? 'text-amber-700' : 'text-blue-700',
      badge: isHighUtil ? 'At Risk' : null,
    },
    {
      id: 'spread',
      label: 'Best Rate Spread',
      value: spread !== null ? `${spread.toFixed(2)}%` : '—',
      sub: spread !== null
        ? `Supply ${bestSupplyRate?.toFixed(2)}% → Borrow ${bestBorrowRate?.toFixed(2)}%`
        : 'No orders configured',
      icon: TrendingUp,
      iconColor: 'text-indigo-600',
      iconBg: 'bg-indigo-50',
      accent: 'border-l-indigo-500',
      valueColor: 'text-indigo-700',
      badge: null,
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 animate-slide-up">
      {cards.map((card) => {
        const CardIcon = card.icon;
        return (
          <div
            key={card.id}
            className={`bg-white rounded-xl border border-gray-200 border-l-4 ${card.accent} p-4 shadow-sm hover:shadow-md transition-all duration-200`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`${card.iconBg} rounded-lg p-2`}>
                <CardIcon size={16} className={card.iconColor} />
              </div>
              {card.badge && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  card.badge === 'At Risk' || card.badge === 'High Demand' ?'bg-amber-100 text-amber-700' :'bg-[#EEE9FF] text-[#6C47FF]'
                }`}>
                  {card.badge}
                </span>
              )}
            </div>
            <p className="section-label mb-1">{card.label}</p>
            <p className={`text-xl font-bold font-mono tabular-nums leading-tight ${card.valueColor}`}>
              {card.value}
            </p>
            <p className="text-xs text-gray-400 mt-1 leading-tight">{card.sub}</p>
          </div>
        );
      })}
    </div>
  );
}